import { meanFromSum } from 'common/helpers/';
import {
  isPlausibleRemoteJitter,
  toFiniteNumber,
} from 'features/analytics/helpers/callScoreCurves';
import type {
  CallStats,
  CallStatsSamples,
  RunningMean,
  WebRTCStatsData,
} from 'features/analytics/interfaces/';

const EMPTY_MEAN: RunningMean = { sum: 0, count: 0 };

export const createCallStatsSamples = (): CallStatsSamples => ({
  inboundVideoFps: EMPTY_MEAN,
  inboundVideoJitter: EMPTY_MEAN,
  sourceVideoFps: EMPTY_MEAN,
  remoteInboundVideoJitter: EMPTY_MEAN,
  rejectedRemoteVideoJitter: EMPTY_MEAN,
  remoteInboundVideoRtt: EMPTY_MEAN,
  remoteInboundAudioJitter: EMPTY_MEAN,
  rejectedRemoteAudioJitter: EMPTY_MEAN,
  remoteInboundAudioRtt: EMPTY_MEAN,
});

const addSample = (
  mean: RunningMean,
  value: number | undefined
): RunningMean =>
  value === undefined ? mean : { sum: mean.sum + value, count: mean.count + 1 };

const toMean = ({ sum, count }: RunningMean): number | undefined =>
  count > 0 ? meanFromSum(sum, count) : undefined;

// Zero-fps polls are hold/privacy/camera-off/stalls; stalls are already scored through freezes.
const toPositiveFps = (value: unknown): number | undefined => {
  const fps = toFiniteNumber(value);
  return fps !== undefined && fps > 0 ? fps : undefined;
};

/**
 * Far-end jitter above 1s is left out of the mean when a plausible report exists.
 * When every report is above 1s, those values are what get scored.
 */
const addRemoteJitter = (
  plausible: RunningMean,
  rejected: RunningMean,
  value: unknown
): { plausible: RunningMean; rejected: RunningMean } => {
  const jitter = toFiniteNumber(value);
  if (jitter === undefined) {
    return { plausible, rejected };
  }
  if (isPlausibleRemoteJitter(jitter)) {
    return { plausible: addSample(plausible, jitter), rejected };
  }
  return { plausible, rejected: addSample(rejected, jitter) };
};

const meanRemoteJitter = (
  plausible: RunningMean,
  rejected: RunningMean
): number | undefined => toMean(plausible) ?? toMean(rejected);

export const addCallStatsSample = (
  samples: CallStatsSamples,
  statsData: WebRTCStatsData
): CallStatsSamples => {
  const inboundVideo = statsData.video?.inbound?.[0];
  const outboundVideo = statsData.video?.outbound?.[0];
  const remoteInboundVideo = statsData.remote?.video?.inbound?.[0];
  const remoteInboundAudio = statsData.remote?.audio?.inbound?.[0];
  const remoteVideoJitter = addRemoteJitter(
    samples.remoteInboundVideoJitter,
    samples.rejectedRemoteVideoJitter,
    remoteInboundVideo?.jitter
  );
  const remoteAudioJitter = addRemoteJitter(
    samples.remoteInboundAudioJitter,
    samples.rejectedRemoteAudioJitter,
    remoteInboundAudio?.jitter
  );

  return {
    inboundVideoFps: addSample(
      samples.inboundVideoFps,
      toPositiveFps(inboundVideo?.framesPerSecond)
    ),
    inboundVideoJitter: addSample(
      samples.inboundVideoJitter,
      toFiniteNumber(inboundVideo?.jitter)
    ),
    sourceVideoFps: addSample(
      samples.sourceVideoFps,
      toPositiveFps(outboundVideo?.track?.framesPerSecond)
    ),
    remoteInboundVideoJitter: remoteVideoJitter.plausible,
    rejectedRemoteVideoJitter: remoteVideoJitter.rejected,
    remoteInboundVideoRtt: addSample(
      samples.remoteInboundVideoRtt,
      toFiniteNumber(remoteInboundVideo?.roundTripTime)
    ),
    remoteInboundAudioJitter: remoteAudioJitter.plausible,
    rejectedRemoteAudioJitter: remoteAudioJitter.rejected,
    remoteInboundAudioRtt: addSample(
      samples.remoteInboundAudioRtt,
      toFiniteNumber(remoteInboundAudio?.roundTripTime)
    ),
  };
};

const withDefinedOverrides = <T extends object>(
  target: T | undefined,
  overrides: Partial<T>
): T | undefined => {
  const defined = Object.entries(overrides).filter(
    ([, value]) => value !== undefined
  );
  return defined.length > 0
    ? ({ ...target, ...Object.fromEntries(defined) } as T)
    : target;
};

/** Replaces end-of-call instantaneous stats with their call-wide mean. */
export const applyCallStatsAverages = (
  callStats: CallStats,
  samples: CallStatsSamples | undefined
): CallStats => {
  if (!samples) {
    return callStats;
  }

  return {
    ...callStats,
    inboundRtpVideo: withDefinedOverrides(callStats.inboundRtpVideo, {
      framesPerSecond: toMean(samples.inboundVideoFps),
      jitter: toMean(samples.inboundVideoJitter),
    }),
    outboundRtpVideo: withDefinedOverrides(callStats.outboundRtpVideo, {
      sourceFramesPerSecond: toMean(samples.sourceVideoFps),
    }),
    remoteInboundRtpVideo: withDefinedOverrides(
      callStats.remoteInboundRtpVideo,
      {
        jitter: meanRemoteJitter(
          samples.remoteInboundVideoJitter,
          samples.rejectedRemoteVideoJitter
        ),
        roundTripTime: toMean(samples.remoteInboundVideoRtt),
      }
    ),
    remoteInboundRtpAudio: withDefinedOverrides(
      callStats.remoteInboundRtpAudio,
      {
        jitter: meanRemoteJitter(
          samples.remoteInboundAudioJitter,
          samples.rejectedRemoteAudioJitter
        ),
        roundTripTime: toMean(samples.remoteInboundAudioRtt),
      }
    ),
  };
};
